import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, finalize, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface UserInfo {
  id: string;
  email: string;
  fullName: string;
}

export interface TokenResponseData {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiry: string;
  refreshTokenExpiry: string;
  user: UserInfo;
}

export interface AuthApiResponse<T> {
  statusCode: number;
  success: boolean;
  message: string;
  data: T;
}

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const ACCESS_TOKEN_EXPIRY_KEY = 'access_token_expiry';
const REFRESH_TOKEN_EXPIRY_KEY = 'refresh_token_expiry';
const USER_KEY = 'user';

// Refresh this many milliseconds before the access token actually expires.
const REFRESH_THRESHOLD_MS = 30_000;
const CLOCK_SKEW_MS = 5_000;
const TRANSIENT_RETRY_MS = 15_000;

@Injectable({
  providedIn: 'root'
})
export class Auth {
  private baseUrl = `${environment.apiUrl}/auth`;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshInFlight$: Observable<AuthApiResponse<TokenResponseData>> | null = null;
  private sessionEpoch = 0;

  constructor(private http: HttpClient, private router: Router) {
    this.scheduleRefresh();
  }

  login(payload: LoginRequest): Observable<AuthApiResponse<TokenResponseData>> {
    return this.http
      .post<AuthApiResponse<TokenResponseData>>(`${this.baseUrl}/login`, payload)
      .pipe(tap((res) => this.setSession(res.data)));
  }

  refreshAccessToken(): Observable<AuthApiResponse<TokenResponseData>> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            statusText: 'No refresh token',
            url: `${this.baseUrl}/refresh-token`
          })
      );
    }

    const epoch = this.sessionEpoch;
    this.refreshInFlight$ = this.http
      .post<AuthApiResponse<TokenResponseData>>(`${this.baseUrl}/refresh-token`, { refreshToken })
      .pipe(
        tap((res) => {
          if (this.sessionEpoch !== epoch) {
            return;
          }
          this.setSession(res.data);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    return this.refreshInFlight$;
  }

  ensureFreshAccessToken(): void {
    if (this.hasValidAccessToken() || !this.hasValidRefreshToken()) {
      return;
    }

    this.refreshAccessToken().subscribe({
      error: (err) => this.handleRefreshFailure(err)
    });
  }

  logout(): void {
    const refreshToken = this.getRefreshToken();
    this.clearSessionAndRedirect();
    this.http.post(`${this.baseUrl}/logout`, { refreshToken }).subscribe({
      error: () => {
        /* Client session is already cleared; server logout is best-effort. */
      }
    });
  }

  setSession(data: TokenResponseData): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    localStorage.setItem(ACCESS_TOKEN_EXPIRY_KEY, data.accessTokenExpiry);
    localStorage.setItem(REFRESH_TOKEN_EXPIRY_KEY, data.refreshTokenExpiry);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    this.scheduleRefresh();
  }

  clearSession(): void {
    this.sessionEpoch += 1;
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ACCESS_TOKEN_EXPIRY_KEY);
    localStorage.removeItem(REFRESH_TOKEN_EXPIRY_KEY);
    localStorage.removeItem(USER_KEY);
    this.refreshInFlight$ = null;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  getUser(): UserInfo | null {
    const stored = localStorage.getItem(USER_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  isLoggedIn(): boolean {
    return this.hasValidAccessToken() || this.hasValidRefreshToken();
  }

  hasValidRefreshToken(): boolean {
    const token = this.getRefreshToken();
    if (!token) return false;

    const expiry = localStorage.getItem(REFRESH_TOKEN_EXPIRY_KEY);
    if (!expiry) return true;

    return new Date(expiry).getTime() + CLOCK_SKEW_MS > Date.now();
  }

  private hasValidAccessToken(): boolean {
    const token = this.getAccessToken();
    const expiry = localStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY);
    if (!token || !expiry) return false;
    return new Date(expiry).getTime() > Date.now();
  }

  private clearSessionAndRedirect(): void {
    this.clearSession();
    this.router.navigate(['/']);
  }

  private handleRefreshFailure(error: unknown): void {
    if (this.isInvalidRefreshError(error)) {
      this.clearSessionAndRedirect();
      return;
    }

    if (!this.hasValidRefreshToken()) {
      return;
    }

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    this.refreshTimer = setTimeout(() => {
      this.refreshAccessToken().subscribe({
        error: (err) => this.handleRefreshFailure(err)
      });
    }, TRANSIENT_RETRY_MS);
  }

  private isInvalidRefreshError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (error.status === 401 || error.status === 400);
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (!this.hasValidRefreshToken()) {
      return;
    }

    const runRefresh = () => {
      this.refreshAccessToken().subscribe({
        error: (err) => this.handleRefreshFailure(err)
      });
    };

    const expiry = localStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY);
    const msUntilRefresh = expiry
      ? new Date(expiry).getTime() - Date.now() - REFRESH_THRESHOLD_MS
      : 0;

    if (msUntilRefresh <= 0) {
      runRefresh();
      return;
    }

    this.refreshTimer = setTimeout(runRefresh, msUntilRefresh);
  }
}
